<template>
  <div class="interview-time-range" :class="[showMessage && 'is-message', currentErrorMessage && 'is-error']">
    <div class="date-select">
      <InterviewDateSelect v-model="startDate" :error="showStartDateError" @change="handleDateChange">
        <template #leftIcon>
          <van-icon class="datetime-icon el-icon-date" />
        </template>
      </InterviewDateSelect>
      <el-button class="delete-time-btn" type="text" @click="handleDeleteTime">
        <van-icon class="icomoon-remove-box icon-btn"></van-icon>
      </el-button>
    </div>
    <div class="time-range">
      <div class="start-time">
        <InterviewDateSelect v-model="startTime" dateType="time" :error="showStartTimeError" :dateTimeProps="dateTimeProps" :events="dateTimeEvents" @change="handleStartTimeChange">
          <template #leftIcon>
            <van-icon class="datetime-icon el-icon-time" />
          </template>
        </InterviewDateSelect>
      </div>
      <span class="time-right"><i class="iconfont iconTo"></i></span>
      <div class="end-time" @click="handleEndTimeClick">
        <InterviewDateSelect v-model="endTime" dateType="time" disabled>
          <template #leftIcon>
            <van-icon class="datetime-icon el-icon-time" />
          </template>
        </InterviewDateSelect>
      </div>
    </div>
    <div v-if="currentErrorMessage" class="message error">{{ currentErrorMessage }}</div>
    <div v-if="showMessage" class="message">{{ $t(`${i18nPrefix}.message.changeDuration`) }}</div>
  </div>
</template>

<script>
import moment from 'moment'
import InterviewDateSelect from '@/components/interview/mobile/interviewDateSelect.vue'
import _ from 'lodash'

export default {
  components: {
    InterviewDateSelect
  },
  props: {
    durationMinutes: {
      type: Number,
      default: 0
    },
    interviewDateTime: {
      type: Object,
      default: () => ({})
    },
    index: {
      type: Number,
      default: 0
    },
    errorMessage: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      showCalendar: false,
      startDate: null,
      startTime: null,
      error: null,
      showStartDateError: false,
      showStartTimeError: false,
      showMessage: false,
      datePickerOptions: {
        disabledDate (time) {
          return time.getTime() < new Date().getTime() - 24 * 60 * 60 * 1000 // 24 hours
        }
      },
      minDate: new Date()
    }
  },
  computed: {
    dateTimeProps () {
      const hourOptions = this.generateRange(this.startMinHour, 24)
      const startHour = this.startTime ? moment(this.startTime, 'HH:mm').format('HH') : hourOptions[0]
      const minuteOptions = this.generateRange(startHour > this.startMinHour ? 0 : this.startMinMinute, 60)
      const startMinute = this.startTime ? moment(this.startTime, 'HH:mm').format('mm') : minuteOptions[0]
      return {
        columns: [
          {
            values: hourOptions,
            defaultIndex: _.indexOf(hourOptions, startHour)
          },
          {
            values: minuteOptions,
            defaultIndex: _.indexOf(minuteOptions, startMinute)
          }
        ],
        title: this.$t(`${this.i18nPrefix}.label.selectTime`)
      }
    },
    dateTimeEvents () {
      return {
        handleTimeChange: (piker, values) => {
          if (values[0] > this.startMinHour) {
            piker.setColumnValues(1, this.generateRange(0, 60))
          } else {
            piker.setColumnValues(1, this.generateRange(this.startMinMinute, 60))
          }
        }
      }
    },
    startMinHour () {
      const isToDay = moment(this.startDate).isSame(moment(), 'day')
      return isToDay ? moment().hour() : 0
    },
    startMinMinute () {
      const isToDay = moment(this.startDate).isSame(moment(), 'day')
      return isToDay ? moment().minute() : 0
    },
    endTime () {
      return this.startTime ? moment(this.startTime, 'HH:mm').add(this.durationMinutes, 'minutes').format('HH:mm') : ''
    },
    currentErrorMessage () {
      return this.showMessage ? '' : (this.error || this.errorMessage)
    }
  },
  created () {
    this.startDate = this.interviewDateTime.date
    this.startTime = this.interviewDateTime.time ? moment(this.interviewDateTime.time, 'HH:mm').format('HH:mm') : null
  },
  methods: {
    handleDateChange () {
      this.startTime = null
      this.$emit('change', {
        ...this.interviewDateTime,
        date: this.startDate,
        time: null,
        dateTime: this.startDate
      })
    },
    handleStartTimeChange (value) {
      this.startTime = value
      this.$emit('change', {
        ...this.interviewDateTime,
        date: this.startDate,
        time: value,
        dateTime: `${this.startDate} ${value}`
      })
    },
    submit () {
      this.showMessage = false
      let valid = true
      if (!this.startDate) {
        valid = false
        this.showStartDateError = true
        this.error = this.$t('common.message.requiredFormField')
      }
      if (!this.startTime) {
        valid = false
        this.showStartTimeError = true
        this.error = this.$t('common.message.requiredFormField')
      }
      return valid
    },
    handleDeleteTime () {
      this.$emit('deleteTime')
    },
    handleEndTimeClick () {
      this.showStartDateError = false
      this.showStartTimeError = false
      this.error = null
      this.showMessage = true
      this.$emit('endTimeClick')
      setTimeout(() => {
        this.showMessage = false
      }, 3000)
    },
    generateRange (start, end) {
      return _.range(start, end).map(minute => {
        return minute.toString().padStart(2, '0')
      })
    }
  },
  watch: {
    errorMessage (value) {
      this.showMessage = false
    }
  }
}
</script>

<style scoped lang="scss">
.interview-time-range {
  position: relative;
  border-radius: 4px;
  border: 1px solid rgba(194, 197, 203, 0.3);
  padding: 16px;

  .date-select {
    display: flex;

    .interview-date-select {
      flex: 1;
    }

    .delete-time-btn {
      margin-left: 20px;
      padding: 0;

      .icon-btn {
        font-size: 20px;

        &::before {
          color: #0096cc;
        }
      }
    }
  }

  .time-range {
    display: flex;
    align-items: center;
    margin-top: 10px;

    .start-time {
    }

    .time-right {
      padding: 0 12px;
    }

    .end-time {
    }
  }

  .message {
    position: absolute;
    right: 16px;
    padding-top: 8px;
    color: #0096cc;
    font-size: 12px;
    line-height: 1;

    &.error {
      left: 16px;
      color: #f56c6c;
    }
  }

  .datetime-icon {
    color: #c2c5cb;
  }

  &.is-error {
    padding-bottom: 28px;
    border-color: #f56c6c7b;
  }

  &.is-message {
    padding-bottom: 28px;
  }
}
</style>
